// src/api/notifications/smsApi.ts
// which number belongs to the clinic.
import { ENV } from '../../config/runtimeEnv';
import type {
	SmsSettings,
	UpdateSmsSettingsPayload,
	SmsNumber,
	SmsCredits,
	SmsUsageMessage,
	SmsPaginated,
	SmsCreditRequestPayload,
	SmsCreditRequest,
} from '../../types/communications/smsTypes';
import { createServiceClient } from '../../lib/http/client';

const SMS_API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;



// ── Client ─────────────────────────────────────────────────────────────────────

const createApiInstance = () => {
	return createServiceClient({
		baseURL: SMS_API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

export const fetchSmsSettings = async (): Promise<SmsSettings> => {
	const api = createApiInstance();
	const response = await api.get<{ data: SmsSettings }>('/sms/settings');
	return response.data.data;
};

export const updateSmsSettings = async (payload: UpdateSmsSettingsPayload): Promise<SmsSettings> => {
	const api = createApiInstance();
	const response = await api.put<{ data: SmsSettings }>('/sms/settings', payload);
	return response.data.data;
};

export const fetchSmsNumbers = async (): Promise<SmsNumber[]> => {
	const api = createApiInstance();
	const response = await api.get<{ data: SmsNumber[] }>('/sms/numbers');
	return response.data.data;
};

export const fetchSmsCredits = async (): Promise<SmsCredits> => {
	const api = createApiInstance();
	const response = await api.get<{ data: SmsCredits }>('/sms/credits');
	return response.data.data;
};

export const fetchSmsMessages = async (page = 1, perPage = 25): Promise<SmsPaginated<SmsUsageMessage>> => {
	const api = createApiInstance();
	const response = await api.get<SmsPaginated<SmsUsageMessage>>('/sms/messages', {
		params: { page, per_page: perPage },
	});
	return response.data;
};

/**
 * Ask the admin system for more credits, via the clinic's own notification service.
 *
 * The browser never holds the `X-Deployment-Key` — notification relays this on our behalf
 * and returns admin's status and body verbatim on 2xx, or a 502 `{ error }` when admin is
 * unreachable or unconfigured.
 */
export const requestSmsCredits = async (payload: SmsCreditRequestPayload): Promise<SmsCreditRequest> => {
	const api = createApiInstance();
	const response = await api.post<{ data: SmsCreditRequest } | SmsCreditRequest>(
		'/sms/credits/request',
		payload,
	);
	const body = response.data as { data?: SmsCreditRequest };
	return (body?.data ?? response.data) as SmsCreditRequest;
};
